import { MinesweeperClient } from '@p4p2r0/minesweeper-core';
import type { PlayerProfile } from '@p4p2r0/minesweeper-core';
import React, { useEffect, useState } from 'react';

import { LANGS, LANG_LABELS, useI18n, type StringKey } from '../i18n';
import { MoonIcon, SunIcon } from './ThemeIcons';
import { PERF_MODES, THEME_CHOICES, type PerfMode, type ThemeChoice } from '../theme';
import type { Identity } from '../storage';

/** Eye / struck-through eye, the convention everywhere else for revealing a secret. */
function EyeIcon({ open }: { open: boolean }): React.ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
      <circle cx="12" cy="12" r="2.6" />
      {open ? null : <path d="M4 20 20 4" />}
    </svg>
  );
}

const THEME_ICON: Record<ThemeChoice, React.ReactElement> = {
  light: <SunIcon />,
  dark: <MoonIcon />,
};

const PERF_LABEL_KEY: Record<PerfMode, StringKey> = {
  auto: 'perf.auto',
  low: 'perf.low',
};

const FEEDBACK_EMAIL = 'p4p2r0@proton.me';

interface AccountPanelProps {
  apiUrl: string;
  identity: Identity | null;
  onIdentity: (identity: Identity | null) => void;
  /** Bumped after a submitted win, so a fresh streak/win count is refetched. */
  refreshKey: number;
  theme: ThemeChoice;
  onTheme: (theme: ThemeChoice) => void;
  lang: (typeof LANGS)[number];
  onLang: (lang: (typeof LANGS)[number]) => void;
  perf: PerfMode;
  onPerf: (perf: PerfMode) => void;
  soundEnabled: boolean;
  onSoundEnabled: (enabled: boolean) => void;
}

export function AccountPanel({
  apiUrl,
  identity,
  onIdentity,
  refreshKey,
  theme,
  onTheme,
  lang,
  onLang,
  perf,
  onPerf,
  soundEnabled,
  onSoundEnabled,
}: AccountPanelProps): React.ReactElement {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keyVisible, setKeyVisible] = useState(false);
  const [profile, setProfile] = useState<PlayerProfile | null>(null);

  useEffect(() => {
    if (!identity) {
      setProfile(null);
      return;
    }
    let cancelled = false;
    new MinesweeperClient({ baseUrl: apiUrl, token: identity.token })
      .me()
      .then((result) => {
        if (!cancelled) setProfile(result);
      })
      .catch(() => {
        // The identity strip already has enough to show; stats are a bonus.
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, identity, refreshKey]);

  const run = async (action: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  // Theme, language and performance are device preferences, not account
  // settings — their own section regardless of whether anyone is signed in,
  // so they never end up crammed against "sign out" with nothing to tell the
  // two apart.
  const preferences = (
    <section className="panel">
      <div className="panel-head">
        <h2 className="panel-title">{t('account.preferences')}</h2>
      </div>

      <div className="pref-row">
        <span className="faint small">{t('nav.theme')}</span>
        <div className="theme-switch" role="radiogroup" aria-label={t('nav.theme')}>
          {THEME_CHOICES.map((choice) => (
            <button
              key={choice}
              type="button"
              role="radio"
              aria-checked={theme === choice}
              className={theme === choice ? 'theme-option active' : 'theme-option'}
              onClick={() => onTheme(choice)}
              aria-label={t(`theme.${choice}` as StringKey)}
              title={t(`theme.${choice}` as StringKey)}
            >
              {THEME_ICON[choice]}
            </button>
          ))}
        </div>
      </div>

      <div className="pref-row">
        <span className="faint small">{t('nav.language')}</span>
        <div className="theme-switch" role="radiogroup" aria-label={t('nav.language')}>
          {LANGS.map((id) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={lang === id}
              className={lang === id ? 'theme-option active wide' : 'theme-option wide'}
              onClick={() => onLang(id)}
            >
              {LANG_LABELS[id]}
            </button>
          ))}
        </div>
      </div>

      <div className="pref-row">
        <span className="faint small">{t('nav.sound')}</span>
        <div className="theme-switch" role="radiogroup" aria-label={t('nav.sound')}>
          <button
            type="button"
            role="radio"
            aria-checked={soundEnabled}
            className={soundEnabled ? 'theme-option active wide' : 'theme-option wide'}
            onClick={() => onSoundEnabled(true)}
          >
            {t('sound.on')}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={!soundEnabled}
            className={!soundEnabled ? 'theme-option active wide' : 'theme-option wide'}
            onClick={() => onSoundEnabled(false)}
          >
            {t('sound.off')}
          </button>
        </div>
      </div>

      <div className="pref-row">
        <span className="faint small">{t('nav.performance')}</span>
        <div className="theme-switch" role="radiogroup" aria-label={t('nav.performance')}>
          {PERF_MODES.map((mode) => (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={perf === mode}
              className={perf === mode ? 'theme-option active wide' : 'theme-option wide'}
              onClick={() => onPerf(mode)}
            >
              {t(PERF_LABEL_KEY[mode])}
            </button>
          ))}
        </div>
      </div>
      <p className="faint small perf-hint">{t('perf.hint')}</p>
    </section>
  );

  // A way to reach the developer directly — a plain mailto link rather than
  // an in-app form, so there's no message-sending backend to build or abuse.
  // Shown regardless of sign-in state; a bug report shouldn't require an
  // account.
  const feedback = (
    <section className="panel">
      <div className="panel-head">
        <h2 className="panel-title">{t('account.feedback')}</h2>
      </div>
      <p className="faint small feedback-hint">{t('account.feedbackHint')}</p>
      <a className="btn ghost" href={`mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent('Minesweeper feedback')}`}>
        {t('account.reportBug')}
      </a>
    </section>
  );

  if (identity) {
    return (
      <>
        {profile ? (
          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">{t('account.status')}</h2>
            </div>

            <div className="level-row">
              <span className="level-badge">{t('account.level', { n: profile.level })}</span>
              <div className="xp-bar">
                <div
                  className="xp-bar-fill"
                  style={{ width: `${(profile.xpIntoLevel / profile.xpForNext) * 100}%` }}
                />
              </div>
              <span className="faint small xp-label">
                {t('account.xpProgress', { have: profile.xpIntoLevel, need: profile.xpForNext })}
              </span>
            </div>

            <div className="stat-row">
              <div className="stat">
                <span className="stat-value">{profile.gamesPlayed}</span>
                <span className="stat-label">{t('account.played')}</span>
              </div>
              <div className="stat">
                <span className="stat-value">{profile.gamesWon}</span>
                <span className="stat-label">{t('account.wins')}</span>
              </div>
              <div className="stat">
                <span className="stat-value">
                  {profile.currentStreak > 0 ? `🔥 ${profile.currentStreak}` : profile.currentStreak}
                </span>
                <span className="stat-label">{t('account.streak')}</span>
              </div>
              <div className="stat">
                <span className="stat-value">{profile.longestStreak}</span>
                <span className="stat-label">{t('account.bestStreak')}</span>
              </div>
            </div>
          </section>
        ) : null}

        <section className="panel">
          <div className="panel-head">
            <h2 className="panel-title">{t('account.title')}</h2>
          </div>

          <p className="small">
            {t('account.signedInAs')} <strong>{identity.username}</strong>
          </p>

          <p className="faint small">{t('account.keyWarning')}</p>

          <div className="key-row">
            <code className="key">
              {keyVisible ? identity.token : '•'.repeat(identity.token.length)}
            </code>
            <button
              type="button"
              className="icon-btn"
              aria-label={t(keyVisible ? 'account.hide' : 'account.show')}
              title={t(keyVisible ? 'account.hide' : 'account.show')}
              aria-pressed={keyVisible}
              onClick={() => setKeyVisible((v) => !v)}
            >
              <EyeIcon open={keyVisible} />
            </button>
            <button
              type="button"
              className="btn ghost"
              onClick={() => void navigator.clipboard?.writeText(identity.token)}
            >
              {t('account.copy')}
            </button>
          </div>

          <button type="button" className="btn ghost danger" onClick={() => onIdentity(null)}>
            {t('account.signOut')}
          </button>
        </section>

        {preferences}
        {feedback}
      </>
    );
  }

  return (
    <>
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t('account.playRanked')}</h2>
        </div>

        <p className="faint small">{t('account.pickName')}</p>

        <form
          className="row"
          onSubmit={(event) => {
            event.preventDefault();
            if (!name.trim() || busy) return;
            void run(async () => {
              const client = new MinesweeperClient({ baseUrl: apiUrl });
              const result = await client.register(name.trim());
              onIdentity({
                token: result.token,
                username: result.player.username,
                playerId: result.player.id,
              });
            });
          }}
        >
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t('account.username')}
            maxLength={20}
            autoComplete="off"
            aria-label={t('account.username')}
          />
          <button type="submit" className="btn" disabled={busy || name.trim().length === 0}>
            {t('account.create')}
          </button>
        </form>

        <details>
          <summary>{t('account.haveKey')}</summary>
          <form
            className="row"
            onSubmit={(event) => {
              event.preventDefault();
              if (!key.trim() || busy) return;
              void run(async () => {
                const token = key.trim();
                const client = new MinesweeperClient({ baseUrl: apiUrl, token });
                const signedInProfile = await client.me();
                onIdentity({
                  token,
                  username: signedInProfile.username,
                  playerId: signedInProfile.id,
                });
                setKey('');
              });
            }}
          >
            <input
              type="password"
              value={key}
              onChange={(event) => setKey(event.target.value)}
              placeholder={t('account.pasteKey')}
              autoComplete="off"
              aria-label={t('account.pasteKey')}
            />
            <button type="submit" className="btn ghost" disabled={busy || key.trim().length === 0}>
              {t('account.signIn')}
            </button>
          </form>
        </details>

        {error ? <p className="error small">{error}</p> : null}
      </section>

      {preferences}
      {feedback}
    </>
  );
}
